import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2.5),
    borderRadius: 18
  },

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5)
  },

  title: {
    fontWeight: 900,
    color:
      theme.palette.type === "dark"
        ? "#fff"
        : "#0A1628"
  },

  subtitle: {
    marginTop: 4,
    color: theme.palette.text.secondary
  },

  addButton: {
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 800
  },

  grid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(1.5)
  },

  card: {
    padding: theme.spacing(2),
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`
  },

  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },

  locationTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 900
  },

  address: {
    marginTop: theme.spacing(1),
    minHeight: 38,
    color: theme.palette.text.secondary
  },

  meta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary
  },

  actions: {
    display: "flex",
    gap: 4
  },

  empty: {
    padding: theme.spacing(5),
    textAlign: "center",
    color: theme.palette.text.secondary
  },

  professionals: {
    marginTop: theme.spacing(2)
  },

  professionalList: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fill, minmax(220px, 1fr))",
    gap: 6,
    marginTop: theme.spacing(1)
  },

  professionalItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: "2px 8px"
  }
}));

const LocationsManager = () => {
  const classes = useStyles();

  const [locations, setLocations] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [links, setLinks] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  const [form, setForm] = useState({
    name: "",
    address: "",
    active: true
  });

  const [selectedProfIds, setSelectedProfIds] =
    useState([]);

  const loadData = useCallback(async () => {
    try {
      const [
        locationsResponse,
        professionalsResponse,
        linksResponse
      ] = await Promise.all([
        api.get("/agenda-locations"),
        api.get("/users/list"),
        api.get("/professional-locations")
      ]);

      setLocations(
        locationsResponse.data || []
      );

      setProfessionals(
        professionalsResponse.data || []
      );

      setLinks(
        linksResponse.data || []
      );
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openNew = () => {
    setEditingLocation(null);

    setForm({
      name: "",
      address: "",
      active: true
    });

    setSelectedProfIds([]);
    setModalOpen(true);
  };

  const openEdit = location => {
    setEditingLocation(location);

    setForm({
      name: location.name || "",
      address: location.address || "",
      active: location.active !== false
    });

    const ids = links
      .filter(
        link =>
          Number(link.locationId) ===
          Number(location.id)
      )
      .map(link =>
        Number(link.professionalId)
      );

    setSelectedProfIds(ids);
    setModalOpen(true);
  };

  const toggleProfessional = id => {
    const numericId = Number(id);

    setSelectedProfIds(prev =>
      prev.includes(numericId)
        ? prev.filter(
            item => item !== numericId
          )
        : [...prev, numericId]
    );
  };

  const syncProfessionals = async locationId => {
    const jobs = [];

    professionals.forEach(professional => {
      const professionalId =
        Number(professional.id);

      const currentLocationIds = links
        .filter(
          link =>
            Number(link.professionalId) ===
            professionalId
        )
        .map(link =>
          Number(link.locationId)
        );

      const shouldHaveLocation =
        selectedProfIds.includes(
          professionalId
        );

      const hasLocation =
        currentLocationIds.includes(
          Number(locationId)
        );

      if (
        shouldHaveLocation ===
        hasLocation
      ) {
        return;
      }

      const nextLocationIds =
        shouldHaveLocation
          ? Array.from(
              new Set([
                ...currentLocationIds,
                Number(locationId)
              ])
            )
          : currentLocationIds.filter(
              id =>
                id !==
                Number(locationId)
            );

      jobs.push(
        api.post(
          "/professional-locations/sync",
          {
            professionalId,
            locationIds:
              nextLocationIds
          }
        )
      );
    });

    await Promise.all(jobs);
  };

  const handleSave = async () => {
    const name = form.name.trim();

    if (!name) {
      toast.error(
        "Informe o nome da unidade"
      );
      return;
    }

    try {
      let location;

      if (editingLocation) {
        const { data } =
          await api.put(
            `/agenda-locations/${editingLocation.id}`,
            {
              name,
              address:
                form.address.trim(),
              active: form.active
            }
          );

        location = data;
      } else {
        const { data } =
          await api.post(
            "/agenda-locations",
            {
              name,
              address:
                form.address.trim(),
              active: form.active
            }
          );

        location = data;
      }

      await syncProfessionals(
        location.id
      );

      toast.success(
        editingLocation
          ? "Unidade atualizada"
          : "Unidade criada"
      );

      setModalOpen(false);
      await loadData();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeactivate = async location => {
    const confirmed = window.confirm(
      `Desativar a unidade "${location.name}"?`
    );

    if (!confirmed) return;

    try {
      await api.delete(
        `/agenda-locations/${location.id}`
      );

      toast.success(
        "Unidade desativada"
      );

      await loadData();
    } catch (err) {
      toastError(err);
    }
  };

  const professionalCount = locationId =>
    links.filter(
      link =>
        Number(link.locationId) ===
        Number(locationId)
    ).length;

  return (
    <>
      <Paper
        className={classes.root}
        elevation={0}
        variant="outlined"
      >
        <div className={classes.header}>
          <div>
            <Typography
              variant="h6"
              className={classes.title}
            >
              Unidades de atendimento
            </Typography>

            <Typography
              variant="body2"
              className={classes.subtitle}
            >
              Cadastre os locais e defina quais profissionais atendem em cada unidade.
            </Typography>
          </div>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            className={classes.addButton}
            onClick={openNew}
          >
            Nova unidade
          </Button>
        </div>

        {!locations.length ? (
          <div className={classes.empty}>
            <LocationOnIcon
              fontSize="large"
            />

            <Typography variant="body1">
              Nenhuma unidade cadastrada.
            </Typography>
          </div>
        ) : (
          <div className={classes.grid}>
            {locations.map(location => (
              <Paper
                key={location.id}
                className={classes.card}
                elevation={0}
              >
                <div
                  className={
                    classes.cardHeader
                  }
                >
                  <div>
                    <Typography
                      className={
                        classes.locationTitle
                      }
                    >
                      <LocationOnIcon
                        fontSize="small"
                        color="primary"
                      />

                      {location.name}
                    </Typography>

                    <Chip
                      size="small"
                      label={
                        location.active
                          ? "Ativa"
                          : "Inativa"
                      }
                      color={
                        location.active
                          ? "primary"
                          : "default"
                      }
                      style={{
                        marginTop: 8
                      }}
                    />
                  </div>

                  <div
                    className={
                      classes.actions
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        openEdit(location)
                      }
                    >
                      <EditIcon
                        fontSize="small"
                      />
                    </IconButton>

                    {location.active && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDeactivate(
                            location
                          )
                        }
                      >
                        <DeleteOutlineIcon
                          fontSize="small"
                        />
                      </IconButton>
                    )}
                  </div>
                </div>

                <Typography
                  variant="body2"
                  className={classes.address}
                >
                  {location.address ||
                    "Endereço não informado"}
                </Typography>

                <Divider />

                <div className={classes.meta}>
                  <PeopleOutlineIcon
                    fontSize="small"
                  />

                  <Typography variant="body2">
                    {professionalCount(
                      location.id
                    )}{" "}
                    profissional
                    {professionalCount(
                      location.id
                    ) === 1
                      ? ""
                      : "is"}
                  </Typography>
                </div>
              </Paper>
            ))}
          </div>
        )}
      </Paper>

      <Dialog
        open={modalOpen}
        onClose={() =>
          setModalOpen(false)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingLocation
            ? "Editar unidade"
            : "Nova unidade"}
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            label="Nome da unidade"
            value={form.name}
            onChange={e =>
              setForm(prev => ({
                ...prev,
                name: e.target.value
              }))
            }
            variant="outlined"
            fullWidth
            autoFocus
          />

          <TextField
            label="Endereço"
            value={form.address}
            onChange={e =>
              setForm(prev => ({
                ...prev,
                address: e.target.value
              }))
            }
            variant="outlined"
            fullWidth
            multiline
            rows={2}
            style={{ marginTop: 16 }}
          />

          <FormControlLabel
            style={{ marginTop: 10 }}
            control={
              <Switch
                checked={form.active}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    active:
                      e.target.checked
                  }))
                }
                color="primary"
              />
            }
            label="Unidade ativa"
          />

          <Divider
            style={{
              margin: "12px 0 16px"
            }}
          />

          <Typography
            variant="subtitle2"
            style={{
              fontWeight: 800
            }}
          >
            Profissionais desta unidade
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
          >
            Marque quem pode atender clientes neste local.
          </Typography>

          <div
            className={
              classes.professionalList
            }
          >
            {professionals.map(
              professional => (
                <div
                  key={professional.id}
                  className={
                    classes.professionalItem
                  }
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={selectedProfIds.includes(
                          Number(
                            professional.id
                          )
                        )}
                        onChange={() =>
                          toggleProfessional(
                            professional.id
                          )
                        }
                      />
                    }
                    label={
                      professional.name
                    }
                  />
                </div>
              )
            )}
          </div>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setModalOpen(false)
            }
            color="secondary"
          >
            Cancelar
          </Button>

          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LocationsManager;
